import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, AuthResponse, LoginCredentials, RegisterData } from '@/types';
import { authService } from '@/services/authService';
import { storage } from '@/utils/storage';
import { clearTenantInfo, setTenantInfo } from '@/utils/tenant';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: storage.getUser(),
  accessToken: storage.getAccessToken(),
  refreshToken: storage.getRefreshToken(),
  isAuthenticated: !!storage.getAccessToken(),
  loading: false,
  error: null,
};

/**
 * Async Thunk: Login user
 */
export const login = createAsyncThunk<AuthResponse, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

/**
 * Async Thunk: Register user
 */
export const register = createAsyncThunk<AuthResponse, RegisterData>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

/**
 * Async Thunk: Refresh access token
 */
export const refreshToken = createAsyncThunk<{ accessToken: string }, void>(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = storage.getRefreshToken();
      if (!token) {
        throw new Error('No refresh token available');
      }
      const response = await authService.refreshToken(token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Token refresh failed');
    }
  }
);

/**
 * Async Thunk: Get current user
 */
export const getCurrentUser = createAsyncThunk<User, void>(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get user');
    }
  }
);

/**
 * Auth Slice
 * Manages authentication state including user, tokens, and auth status
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      storage.clearAuth();
      clearTenantInfo();
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      storage.setUser(action.payload);
      if (action.payload.tenant) {
        setTenantInfo(action.payload.tenant as any);
      }
    },
    setTokens: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      storage.setAccessToken(action.payload.accessToken);
      storage.setRefreshToken(action.payload.refreshToken);
    },
    /** Token rotation during an active session — does not wipe the API cache (see store rootReducer). */
    renewSessionTokens: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      storage.setAccessToken(action.payload.accessToken);
      storage.setRefreshToken(action.payload.refreshToken);
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const payload: any = action.payload;
        const authData = payload?.tokens ? payload : payload?.data;

        state.loading = false;

        if (!authData?.user || !authData?.tokens) {
          state.error = 'Invalid login response';
          state.isAuthenticated = false;
          return;
        }

        state.user = authData.user;
        state.accessToken = authData.tokens.accessToken;
        state.refreshToken = authData.tokens.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
        
        // Persist to storage
        storage.setUser(authData.user);
        storage.setAccessToken(authData.tokens.accessToken);
        storage.setRefreshToken(authData.tokens.refreshToken);
        
        if (authData.user.tenant) {
          setTenantInfo(authData.user.tenant as any);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        const payload: any = action.payload;
        const authData = payload?.tokens ? payload : payload?.data;

        state.loading = false;

        if (!authData?.user || !authData?.tokens) {
          state.error = 'Invalid registration response';
          state.isAuthenticated = false;
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
          storage.clearAuth();
          clearTenantInfo();
          return;
        }

        state.user = authData.user;
        state.accessToken = authData.tokens.accessToken;
        state.refreshToken = authData.tokens.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
        
        // Persist to storage
        storage.setUser(authData.user);
        storage.setAccessToken(authData.tokens.accessToken);
        storage.setRefreshToken(authData.tokens.refreshToken);
        if (authData.user.tenant) {
          setTenantInfo(authData.user.tenant as any);
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        storage.clearAuth();
        clearTenantInfo();
      });

    // Refresh Token
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        const payload: any = action.payload;
        const tokenData = payload?.accessToken ? payload : payload?.data;

        if (!tokenData?.accessToken) {
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
          storage.clearAuth();
          return;
        }

        state.accessToken = tokenData.accessToken;
        storage.setAccessToken(tokenData.accessToken);
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        storage.clearAuth();
      });

    // Get Current User
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        const payload: any = action.payload;
        const userData = payload?.id ? payload : payload?.data;

        state.loading = false;

        if (!userData?.id) {
          state.error = 'Invalid user profile response';
          state.isAuthenticated = false;
          state.accessToken = null;
          state.refreshToken = null;
          state.user = null;
          storage.clearAuth();
          return;
        }

        const nextUser = userData?.tenant || !state.user?.tenant
          ? userData
          : { ...userData, tenant: state.user.tenant };

        state.user = nextUser;
        state.isAuthenticated = true;
        storage.setUser(nextUser);
        if (nextUser.tenant) {
          setTenantInfo(nextUser.tenant as any);
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        
        // Only clear auth if no valid user exists
        if (!state.user) {
          state.accessToken = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
          storage.clearAuth();
          clearTenantInfo();
        }
      });
  },
});

export const { logout, clearError, updateUser, setTokens, renewSessionTokens } = authSlice.actions;
export default authSlice.reducer;
